// src/components/personal/sections/ClientsSection/components/ClientCard/ClientProducts.tsx
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Table, TableColumn } from '@/components/ui/Table/Table';
import { useColumnWidths } from '@/hooks/useColumnWidths';
import { RefreshCw } from 'lucide-react';

interface ProductType {
  id?: string;
  name: string;
  quantity: number;
}

interface ClientProductsProps {
  products: ProductType[];
}

const ClientProducts: React.FC<ClientProductsProps> = ({ products }) => {
  const { t } = useTranslation(['clients', 'common']);
  
  // Используем хук для сохранения ширины столбцов
  const [columnWidths, setColumnWidths] = useColumnWidths('client-products-table');

  // Определяем колонки для таблицы
  const columns: TableColumn<ProductType>[] = [
    {
      key: 'name',
      title: t('products.table.name'),
      width: 'xl',
      resizable: true,
      wrap: true
    },
    {
      key: 'quantity',
      title: t('products.table.quantity'),
      width: 'sm',
      resizable: true,
      textAlign: 'center'
    }
  ];

  if (!products || products.length === 0) {
    return (
      <div className="text-center py-8 text-secondary">
        {t('products.no_products')}
      </div>
    );
  }

  // Функция для сброса ширины столбцов к значениям по умолчанию
  const resetColumnWidths = () => {
    setColumnWidths({});
  };

  return (
    <div className="space-y-4">
      {/* Кнопка для сброса настроек столбцов */}
      {Object.keys(columnWidths).length > 0 && (
        <div className="flex justify-end mb-2">
          <button 
            onClick={resetColumnWidths}
            className="text-xs text-gray-500 hover:text-primary transition-colors flex items-center"
            title={t('common:reset_to_default')}
          >
            <RefreshCw className="w-3 h-3 mr-1" />
            {t('common:reset_columns')}
          </button>
        </div>
      )}

      <Table
        data={products}
        columns={columns}
        emptyText={t('products.no_products')}
        scrollable={false}
        resizableColumns={true}
        savedColumnWidths={columnWidths}
        onColumnWidthsChange={setColumnWidths}
        // Добавляем адаптивность
        responsive={true}
        mobileBehavior="cards"
      />

      <div className="flex justify-end items-center bg-background p-3 rounded-lg">
        <span className="font-medium mr-4">
          {t('products.total_items')}:
        </span>
        <span className="font-medium text-primary">
          {products.length}
        </span>
      </div>
    </div>
  );
};

export default ClientProducts;